import {capitalize} from 'lodash-es';
import type {ChangeEvent} from 'react';
import {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {toast} from 'react-toastify';

import {ledger} from '@/api/transactions';
import type {AdminAfriexPerson} from '@/types';
import type {ICurrency} from '@/types/Currency';
import type {LedgerReasons} from '@/types/Transaction';
import {getErrorMessage} from '@/utils/errorMessage';

type Response = {
  message: string;
  status: string;
};

const useFundUserWallet = (
  user: AdminAfriexPerson,
  initialResponse: Response,
) => {
  const navigate = useNavigate();
  const [amount, setAmount] = useState<number>(0);
  const [currency, setCurrency] = useState<ICurrency | null>(null);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [response, setResponse] = useState(initialResponse);
  const [action, setAction] = useState<'credit' | 'debit'>('debit');
  const [fundWalletReason, setFundWalletReason] = useState<LedgerReasons>();

  // The rendered control is a `<Select>` (a `<select>` under the hood); a
  // `HTMLFormElement` target has no real `value`, and this project's
  // TypeScript/React setup resolves that generic parameter's members to
  // `any` rather than erroring, which is what let `event.target.value` reach
  // `setFundWalletReason` untyped.
  function handleSelectReason(event: ChangeEvent<HTMLSelectElement>) {
    setFundWalletReason(event.target.value as LedgerReasons);
  }

  const handleAmountChange = (_name: string, value: number) => {
    setAmount(value);
  };

  const handleCurrencyChange = (_name: string, value: ICurrency) => {
    setCurrency(value);
  };

  const handleLedgerButtonClick = (action: 'debit' | 'credit') => {
    setAction(action);
    setShowModal(true);
  };

  // isValidTransaction -> amount is greater than 0, amount is not Nan, fundWalletReason exists, currency exists
  const isValidTransaction = () => {
    if (currency && fundWalletReason && (amount > 0 || !isNaN(amount))) {
      return false;
    }

    return true;
  };

  async function handleLedger() {
    try {
      if (!(currency && fundWalletReason)) {
        return;
      }
      setLoading(true);
      const response = await ledger({
        currency: currency?.code,
        amount: amount,
        action: action,
        userId: user.id,
        reason: fundWalletReason,
      });

      if (response) {
        setLoading(false);
        setShowModal(false);
        setAmount(0);
      }
      toast.success(
        `Successfully ${action}ed ${currency?.code} ${amount} ${
          action === 'debit' ? 'from' : 'to'
        } ${user.name?.firstName} ${user.name?.lastName}'s Wallet`,
      );
      setResponse({
        status: 'success',
        message: `Successfully ${action}ed ${currency?.code} ${amount} ${
          action === 'debit' ? 'from' : 'to'
        } ${user.name?.firstName} ${
          user.name?.lastName
        }'s Wallet for ${capitalize(fundWalletReason?.replace(/_/g, ' '))}`,
      });
      setTimeout(() => {
        // react-router 7 types `navigate` as `void | Promise<void>`.
        // Normalising it keeps the reload fire-and-forget while giving the
        // promise branch a rejection handler; there is nothing left on screen
        // to report a failed re-navigation to.
        Promise.resolve(navigate(0)).catch(() => {});
      }, 1500);
    } catch (e) {
      setLoading(false);
      setShowModal(false);
      const message = getErrorMessage(e, 'Unknown Error! Contact Admin');
      setResponse({status: 'error', message});
    }
  }
  return {
    amount,
    currency,
    loading,
    showModal,
    response,
    action,
    handleAmountChange,
    handleCurrencyChange,
    handleLedgerButtonClick,
    isValidTransaction,
    handleLedger,
    setShowModal,
    setResponse,
    handleSelectReason,
    fundWalletReason,
  };
};

export default useFundUserWallet;
